import { Component, Inject, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { UploadFile } from "@angular-ex/uploader";
import { FileUploaderComponent } from "../../../../shared/components/file-uploader/file-uploader.component";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { CampaignBannerMediaType } from "../../../../shared/enums/campaign.enums";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import {
  BannerEntity,
  BannerFileEntity,
} from "../../../../shared/entities/file/banner.entity";
import { cloneWith } from "../../../../shared/services/clonable";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignService } from "../../shared/services/brand-campaign.service";
import { unsafeCreatePartial } from "../../../../shared/utils/create-partial";
import { AnyToBannerEntityMapper } from "../../../../shared/mappers/banner.entity.mapper";

export interface CampaignContentFormBannerDialogData
  extends BrandCampaignAddContentDialogData {
  readonly banner?: BannerEntity;
}

export type CampaignContentFormBannerDialogResult = BannerEntity | undefined;

interface UploadedFile extends UploadFile {
  isNewUpload: boolean;
  displayUrl: SafeUrl;
}

@Component({
  selector: "app-campaign-content-form-banner-dialog",
  templateUrl: "./campaign-content-form-banner-dialog.component.html",
  styleUrls: ["./campaign-content-form-banner-dialog.component.scss"],
})
export class CampaignContentFormBannerDialogComponent {
  public readonly uploaderUrl: string;
  public readonly uploaderSubtitle: string;
  public readonly uploaderFieldName = "files";
  public readonly isNewBanner: boolean;
  public readonly fileUploaderResponseMapper = AnyToBannerEntityMapper;
  public readonly campaign: BrandCampaign;
  public readonly campaignDetails: BrandCampaignDetails;
  public readonly category: BrandCampaignCategory;
  public saving = false;
  public banner: BannerEntity;
  public fileResponses: any[] = [];
  public uploadData: any = {};
  public uploadFiles: UploadedFile[] = [];
  public deletingFileId?: number;
  private readonly originaryNumFiles: number;

  @ViewChild(FileUploaderComponent)
  private fileUploaderComponent!: FileUploaderComponent;

  constructor(
    private readonly dialogService: DialogService,
    private readonly translateService: TranslateService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly notificationService: NotificationService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormBannerDialogComponent,
      CampaignContentFormBannerDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormBannerDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewBanner = !data.banner;
    this.originaryNumFiles = data.banner?.files.length || 0;

    this.banner = data.banner
      ? cloneWith(data.banner)
      : unsafeCreatePartial(BannerEntity, {
          mediaType: CampaignBannerMediaType.Banner,
        });

    this.fileResponses = [];
    this.uploaderUrl = this.isNewBanner
      ? this.brandCampaignService.getCreateCampaignDetailCategoryImageUrl(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
        )
      : this.brandCampaignService.getUpdateCampaignDetailCategoryImageUrl(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.banner.id,
        );

    this.uploadData = {
      title: this.banner.title,
      media_type: this.banner.mediaType,
    };

    this.uploaderSubtitle =
      this.translateService.instant(
        "brand.campaign.banner.dragAndDropValidExtensions",
      ) + " jpg, jpeg, png";
  }

  public handleUploadResponse(file: BannerEntity): void {
    if (this.isNewBanner) {
      this.category.images = this.category.images.filter(
        (image) => image.id !== file.id,
      );
      this.category.images.push(file);
    }
    this.banner = cloneWith(file);

    if (
      file.files.length ===
      this.uploadFiles.length + this.originaryNumFiles
    ) {
      this.saveBanner();
    } else {
      if (this.banner.id) {
        this.uploadData.image_id = this.banner.id;
      }
      this.fileUploaderComponent.startUploadFile();
    }
  }

  public saveContent(): void {
    this.saving = true;
    this.uploadData.title = this.banner.title;

    if (this.banner.id) {
      this.uploadData.image_id = this.banner.id;
    }

    if (this.uploadFiles.length > 0) {
      this.fileUploaderComponent.startUploadFile();
    } else {
      this.saveBanner();
    }
  }

  public onAfterAddingFile(fileItem: UploadFile): void {
    this.uploadFiles.push({
      ...fileItem,
      isNewUpload: true,
      displayUrl: this.sanitizer.bypassSecurityTrustUrl(
        window.URL.createObjectURL(fileItem.nativeFile as Blob),
      ),
    });
  }

  public deleteFileConfirm(file: UploadedFile | BannerFileEntity): void {
    this.dialogService.confirm({
      title: "brand.campaign.banner.deleteImageTitle",
      message: "brand.campaign.banner.deleteImage",
      onConfirm: () => {
        if ("isNewUpload" in file && file.isNewUpload) {
          this.deleteQueueFile(file.id);
        } else {
          this.deleteBannerFile(+file.id);
        }
      },
    });
  }

  private deleteQueueFile(fileId: string): void {
    this.uploadFiles = this.uploadFiles.filter((file) => file.id !== fileId);
    this.fileUploaderComponent.removeFile(fileId);
  }

  private deleteBannerFile(fileId: number): void {
    this.deletingFileId = fileId;
    this.brandCampaignService
      .deleteCampaignDetailCategoryImageFile(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.banner.id,
        fileId,
      )
      .subscribe({
        next: () => {
          this.deletingFileId = undefined;
          this.banner = cloneWith(this.banner, {
            files: this.banner.files.filter((f) => f.id !== fileId),
          });
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.deletingFileId = undefined;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  private saveBanner(): void {
    this.brandCampaignService
      .updateCampaignDetailCategoryImage(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.banner,
      )
      .subscribe({
        next: (banner) => {
          this.banner = banner;
          this.dialogRef.close(this.banner);
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }
}
